import {
  JobAdmissionError,
  getMsUntilNextMinute,
  isJobAdmissionKillSwitchActive,
  jsonByteLength,
  readNonNegativeIntegerEnv,
  readPositiveIntegerEnv,
} from "@gencow/core";

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;

type ScheduledJobStatusRow = {
  id: string;
  status: string;
};

type ScheduledJobBacklogRow = {
  pending_count: unknown;
  running_count: unknown;
};

export interface ScheduledJobAdmissionOptions {
  rawSql: RawSql;
  appId: number;
  appName: string;
  jobId: string;
  action: string;
  args: unknown;
  runAt: string | Date;
  dedupeKey?: string | null;
  env?: Record<string, string | undefined>;
  now?: () => Date;
}

export interface ScheduledJobAdmissionPolicy {
  pendingPerApp: number;
  runningPerApp: number;
  createsPerMinutePerApp: number;
  createsPerMinutePerAction: number;
  payloadMaxBytes: number;
  minDelayMs: number;
  maxFutureMs: number;
}

export interface ScheduledJobAdmissionDecision {
  dedupeKey?: string;
  existingJobId?: string;
}

export const DEFAULT_SCHEDULED_PENDING_PER_APP = 1_000;
export const DEFAULT_SCHEDULED_RUNNING_PER_APP = 50;
export const DEFAULT_SCHEDULED_CREATES_PER_MINUTE_PER_APP = 120;
export const DEFAULT_SCHEDULED_PAYLOAD_MAX_BYTES = 64 * 1024;
export const DEFAULT_SCHEDULED_MAX_FUTURE_DAYS = 30;
export const DEFAULT_SCHEDULED_DUE_PER_APP_PER_POLL = 20;

function rows<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function numberFrom(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function resolveScheduledJobAdmissionPolicy(
  env: Record<string, string | undefined> = process.env,
): ScheduledJobAdmissionPolicy {
  const maxFutureDays =
    readPositiveIntegerEnv(env, "GENCOW_SCHEDULED_MAX_FUTURE_DAYS") ?? DEFAULT_SCHEDULED_MAX_FUTURE_DAYS;
  return {
    pendingPerApp:
      readPositiveIntegerEnv(env, "GENCOW_SCHEDULED_PENDING_PER_APP") ??
      readPositiveIntegerEnv(env, "GENCOW_JOB_PENDING_PER_APP") ??
      DEFAULT_SCHEDULED_PENDING_PER_APP,
    runningPerApp:
      readPositiveIntegerEnv(env, "GENCOW_SCHEDULED_RUNNING_PER_APP") ?? DEFAULT_SCHEDULED_RUNNING_PER_APP,
    createsPerMinutePerApp:
      readPositiveIntegerEnv(env, "GENCOW_SCHEDULED_CREATES_PER_MINUTE_PER_APP") ??
      readPositiveIntegerEnv(env, "GENCOW_JOB_CREATES_PER_MINUTE_PER_APP") ??
      DEFAULT_SCHEDULED_CREATES_PER_MINUTE_PER_APP,
    createsPerMinutePerAction:
      readPositiveIntegerEnv(env, "GENCOW_SCHEDULED_CREATES_PER_MINUTE_PER_ACTION") ??
      readPositiveIntegerEnv(env, "GENCOW_JOB_CREATES_PER_MINUTE_PER_ACTION") ??
      readPositiveIntegerEnv(env, "GENCOW_SCHEDULED_CREATES_PER_MINUTE_PER_APP") ??
      readPositiveIntegerEnv(env, "GENCOW_JOB_CREATES_PER_MINUTE_PER_APP") ??
      DEFAULT_SCHEDULED_CREATES_PER_MINUTE_PER_APP,
    payloadMaxBytes:
      readPositiveIntegerEnv(env, "GENCOW_SCHEDULED_PAYLOAD_MAX_BYTES") ??
      readPositiveIntegerEnv(env, "GENCOW_JOB_PAYLOAD_MAX_BYTES") ??
      DEFAULT_SCHEDULED_PAYLOAD_MAX_BYTES,
    minDelayMs: readNonNegativeIntegerEnv(env, "GENCOW_SCHEDULED_MIN_DELAY_MS") ?? 0,
    maxFutureMs: maxFutureDays * 24 * 60 * 60 * 1000,
  };
}

export function resolveScheduledDueMaxClaimsPerAppPerPoll(
  env: Record<string, string | undefined> = process.env,
  explicit?: number,
): number {
  if (typeof explicit === "number" && Number.isInteger(explicit) && explicit > 0) return explicit;
  return (
    readPositiveIntegerEnv(env, "GENCOW_SCHEDULED_DUE_PER_APP_PER_POLL") ??
    DEFAULT_SCHEDULED_DUE_PER_APP_PER_POLL
  );
}

export function resolveScheduledMaxRunningPerApp(
  env: Record<string, string | undefined> = process.env,
  explicit?: number,
): number {
  if (typeof explicit === "number" && Number.isInteger(explicit) && explicit > 0) return explicit;
  return readPositiveIntegerEnv(env, "GENCOW_SCHEDULED_RUNNING_PER_APP") ?? DEFAULT_SCHEDULED_RUNNING_PER_APP;
}

async function getExistingScheduledJobStatus(
  rawSql: RawSql,
  appId: number,
  jobId: string,
  action: string,
  dedupeKey?: string,
): Promise<ScheduledJobStatusRow | null> {
  const existing = rows<ScheduledJobStatusRow>(
    await rawSql(
      `SELECT id, status
         FROM scheduled_jobs
        WHERE app_id = $1
          AND (
            id = $2
            OR ($3::text IS NOT NULL AND action = $4 AND dedupe_key = $3)
          )
        ORDER BY CASE WHEN id = $2 THEN 0 ELSE 1 END
        LIMIT 1`,
      [appId, jobId, dedupeKey ?? null, action],
    ),
  )[0];
  return existing ?? null;
}

async function getScheduledJobBacklog(
  rawSql: RawSql,
  appId: number,
): Promise<{ pending: number; running: number }> {
  const row = rows<ScheduledJobBacklogRow>(
    await rawSql(
      `SELECT
          COUNT(*) AS total_count,
          COALESCE(SUM(CASE WHEN status = 'pending' THEN 1 ELSE 0 END), 0)::int AS pending_count,
          COALESCE(SUM(CASE WHEN status = 'running' THEN 1 ELSE 0 END), 0)::int AS running_count
       FROM scheduled_jobs
       WHERE app_id = $1`,
      [appId],
    ),
  )[0];
  return {
    pending: numberFrom(row?.pending_count),
    running: numberFrom(row?.running_count),
  };
}

async function consumeScheduledRateBucket(options: {
  rawSql: RawSql;
  scope: string;
  limit: number;
  now: Date;
}): Promise<boolean> {
  const result = await options.rawSql(
    `INSERT INTO platform_job_admission_buckets (scope, bucket_start, count, updated_at)
       VALUES ($1, date_trunc('minute', $2::timestamptz), 1, NOW())
       ON CONFLICT (scope, bucket_start) DO UPDATE
         SET count = platform_job_admission_buckets.count + 1,
             updated_at = NOW()
         WHERE platform_job_admission_buckets.count < $3
       RETURNING count`,
    [options.scope, options.now.toISOString(), options.limit],
  );
  return rows(result).length > 0;
}

async function acquireScheduledCreateAdmissionLock(rawSql: RawSql, appId: number): Promise<void> {
  await rawSql(`SELECT pg_advisory_xact_lock(hashtextextended($1::text, 0))`, [
    `scheduled:create:app:${appId}`,
  ]);
}

function normalizeDedupeKey(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed.slice(0, 512);
}

function isScheduledCreateKillSwitchActive(
  env: Record<string, string | undefined>,
  options: Pick<ScheduledJobAdmissionOptions, "appId" | "appName" | "action">,
): boolean {
  return [
    "scheduled:create",
    `scheduled:create:app:${options.appId}`,
    `scheduled:create:app-name:${options.appName}`,
    `scheduled:create:action:${options.action}`,
    `scheduled:create:app:${options.appId}:action:${options.action}`,
    `scheduled:create:app-name:${options.appName}:action:${options.action}`,
  ].some((scope) => isJobAdmissionKillSwitchActive(env, scope));
}

export async function applyScheduledJobAdmission(
  options: ScheduledJobAdmissionOptions,
): Promise<ScheduledJobAdmissionDecision> {
  const env = options.env ?? process.env;
  const now = options.now?.() ?? new Date();
  const policy = resolveScheduledJobAdmissionPolicy(env);
  const dedupeKey = normalizeDedupeKey(options.dedupeKey);

  if (isScheduledCreateKillSwitchActive(env, options)) {
    throw new JobAdmissionError("job_admission_disabled", "scheduled job admission is disabled");
  }

  const runAt = options.runAt instanceof Date ? options.runAt : new Date(options.runAt);
  if (!Number.isFinite(runAt.getTime())) {
    throw new JobAdmissionError("job_schedule_horizon_exceeded", "scheduled job runAt must be a valid date");
  }

  const delayMs = runAt.getTime() - now.getTime();
  if (policy.minDelayMs > 0 && delayMs >= 0 && delayMs < policy.minDelayMs) {
    throw new JobAdmissionError(
      "job_min_delay_violation",
      "scheduled job delay is below the platform minimum",
      {
        details: { minDelayMs: policy.minDelayMs, delayMs },
      },
    );
  }
  if (delayMs > policy.maxFutureMs) {
    throw new JobAdmissionError(
      "job_schedule_horizon_exceeded",
      "scheduled job runAt exceeds the platform horizon",
      {
        details: { maxFutureMs: policy.maxFutureMs, delayMs },
      },
    );
  }

  const payloadBytes = jsonByteLength({ action: options.action, args: options.args });
  if (payloadBytes > policy.payloadMaxBytes) {
    throw new JobAdmissionError("job_payload_too_large", "scheduled job payload is too large", {
      details: { limitBytes: policy.payloadMaxBytes, payloadBytes },
    });
  }

  await acquireScheduledCreateAdmissionLock(options.rawSql, options.appId);

  const existing = await getExistingScheduledJobStatus(
    options.rawSql,
    options.appId,
    options.jobId,
    options.action,
    dedupeKey,
  );
  if (existing?.status === "running") {
    throw new JobAdmissionError("job_backlog_limit_exceeded", "scheduled job is already running", {
      status: 409,
      details: { jobId: existing.id },
    });
  }
  if (existing) return { dedupeKey, existingJobId: existing.id };

  const backlog = await getScheduledJobBacklog(options.rawSql, options.appId);
  if (backlog.pending >= policy.pendingPerApp) {
    throw new JobAdmissionError(
      "job_backlog_limit_exceeded",
      "scheduled job pending backlog limit exceeded",
      {
        details: {
          appId: options.appId,
          appName: options.appName,
          limit: policy.pendingPerApp,
          pending: backlog.pending,
        },
      },
    );
  }
  if (backlog.running >= policy.runningPerApp) {
    throw new JobAdmissionError(
      "job_backlog_limit_exceeded",
      "scheduled job running backlog limit exceeded",
      {
        details: {
          appId: options.appId,
          appName: options.appName,
          limit: policy.runningPerApp,
          running: backlog.running,
        },
      },
    );
  }

  const consumed = await consumeScheduledRateBucket({
    rawSql: options.rawSql,
    scope: `scheduled:create:app:${options.appId}`,
    limit: policy.createsPerMinutePerApp,
    now,
  });
  if (!consumed) {
    throw new JobAdmissionError("job_rate_limited", "scheduled job create rate limit exceeded", {
      retryAfterMs: getMsUntilNextMinute(now),
      details: { appId: options.appId, appName: options.appName, limit: policy.createsPerMinutePerApp },
    });
  }

  const actionConsumed = await consumeScheduledRateBucket({
    rawSql: options.rawSql,
    scope: `scheduled:create:app:${options.appId}:action:${options.action}`,
    limit: policy.createsPerMinutePerAction,
    now,
  });
  if (!actionConsumed) {
    throw new JobAdmissionError("job_rate_limited", "scheduled job action create rate limit exceeded", {
      retryAfterMs: getMsUntilNextMinute(now),
      details: {
        appId: options.appId,
        appName: options.appName,
        action: options.action,
        limit: policy.createsPerMinutePerAction,
      },
    });
  }

  void options
    .rawSql(`DELETE FROM platform_job_admission_buckets WHERE updated_at < NOW() - INTERVAL '2 hours'`)
    .catch(() => {});

  return { dedupeKey };
}
